import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import { batchActions } from "@obg/ngrx-utils";
import {
	BETTING_LIMIT_MESSAGE_CONTEXT,
	BettingLimit,
	BettingLimitAction,
	BettingLimitMessageCodes,
	BettingLimitSelector,
	BettingLimitType,
	CommonState,
	LIMIT_PERIODS,
	LimitPeriod,
	ResponsibleGamingUiConfigService,
	TimedBettingLimitParams,
	TRANSLATION_PREFIX,
} from "@obg/ngx.common";
import {
	AbstractAlertButtonStyle,
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	Currency,
	CurrencySelector,
	JurisdictionPrefixService,
	MessageService,
	MessageType,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { OBG_VALIDATORS } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { merge, Subscription } from "rxjs";
import { switchMap, take, tap } from "rxjs/operators";

import { ResponsibleGamingFormService } from "../containers/responsible-gaming-form.service";

import { BettingLimitChangeComponent } from "./change/betting-limit-change.component";
import { BETTING_LIMIT_CHANGE_CLASS, BETTING_LIMIT_CHANGE_DIALOG_ID } from "./change/betting-limit-change.const";

@Component({
	selector: "obg-m-betting-limit-container",
	templateUrl: "./betting-limit.container.html",
	styleUrls: ["./betting-limit.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class BettingLimitContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-betting-limit-container") hostClass = true;

	readonly bettingLimitType = {
		betCountLimit: BettingLimitType.BetCountLimit,
		wageringLimit: BettingLimitType.WageringLimit,
		timedWageringLimit: BettingLimitType.TimedWageringLimit,
		betSizeLimit: BettingLimitType.BetSizeLimit,
	};
	readonly wageringLimitPeriod = {
		daily: LimitPeriod.Daily,
		weekly: LimitPeriod.Weekly,
		monthly: LimitPeriod.Monthly,
	};
	isSetBettingLimitLoading = false;
	isRemoveBettingLimitLoading = false;
	activeBettingLimit: BettingLimit | undefined;
	queuedBettingLimit: BettingLimit | undefined;
	currency: Currency | undefined;
	bettingLimitForm = this.formBuilder.group({
		limit: this.formBuilder.control("", [Validators.required, OBG_VALIDATORS.numeric, Validators.maxLength(12)]),
		limitType: this.formBuilder.control(BettingLimitType.WageringLimit, Validators.required),
	});
	isBettingLimitEdited = false;
	isDeleteLimitEnabled = this.responsibleGamingUiConfigService.getShowBettingLimitDeleteBtn();
	private data$$: Subscription | undefined;
	private isFormDirty$$: Subscription | undefined;

	constructor(
		private formBuilder: UntypedFormBuilder,
		private store: Store<CommonState>,
		private currencySelector: CurrencySelector,
		private alertService: AbstractTranslatableAlertService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private changeDetectorRef: ChangeDetectorRef,
		private jurisdictionPrefixService: JurisdictionPrefixService,
		private messageService: MessageService,
		private responsibleGamingUiConfigService: ResponsibleGamingUiConfigService,
		private responsibleGamingFormService: ResponsibleGamingFormService
	) {}

	ngOnInit() {
		const timedWageringLimit$ = this.responsibleGamingUiConfigService.getIsTimedWageringLimit$().pipe(
			tap((isTimed) => {
				if (isTimed) {
					this.getBettingLimits(true);
					this.bettingLimitForm.controls.limitType.setValue(BettingLimitType.TimedWageringLimit);
					this.bettingLimitForm.addControl(
						"limitPeriod",
						this.formBuilder.control(LimitPeriod.Daily, [Validators.required])
					);
					return;
				}
				this.getBettingLimits(false);
			})
		);

		const activeBettingLimit$ = this.store.pipe(
			select(BettingLimitSelector.getActiveBettingLimit),
			ignoreNil(),
			tap((activeBettingLimit) => {
				this.isSetBettingLimitLoading = false;
				this.activeBettingLimit = activeBettingLimit;
			})
		);

		const queuedBettingLimit$ = this.store.pipe(
			select(BettingLimitSelector.getQueuedBettingLimit),
			ignoreNil(),
			tap((queuedBettingLimit) => {
				this.isSetBettingLimitLoading = false;
				this.queuedBettingLimit = queuedBettingLimit;
			})
		);

		const currency$ = this.store.pipe(
			select(this.currencySelector.getByCurrentCustomer()),
			tap((currency) => (this.currency = currency))
		);

		const deleteBetCountLimitSuccess$ = this.messageService
			.consume$({
				context: [BETTING_LIMIT_MESSAGE_CONTEXT.deleteBetCountLimit],
				messageType: MessageType.Success,
			})
			.pipe(
				switchMap(() =>
					this.jurisdictionPrefixService.addJurisdictionPrefix$(
						`${TRANSLATION_PREFIX}.${BettingLimitMessageCodes.DeleteBetCountLimitSuccess}`
					)
				),
				tap((message) => this.handleLimitDeletionSuccess(message))
			);

		const deleteWageringLimitSuccess$ = this.messageService
			.consume$({
				context: [BETTING_LIMIT_MESSAGE_CONTEXT.deleteWageringLimit],
				messageType: MessageType.Success,
			})
			.pipe(
				switchMap(() =>
					this.jurisdictionPrefixService.addJurisdictionPrefix$(
						`${TRANSLATION_PREFIX}.${BettingLimitMessageCodes.DeleteWageringLimitSuccess}`
					)
				),
				tap((message) => this.handleLimitDeletionSuccess(message))
			);

		const deleteTimedWageringLimitSuccess$ = this.messageService
			.consume$({
				context: [BETTING_LIMIT_MESSAGE_CONTEXT.deleteTimedWageringLimit],
				messageType: MessageType.Success,
			})
			.pipe(
				switchMap(() =>
					this.jurisdictionPrefixService.addJurisdictionPrefix$(
						`${TRANSLATION_PREFIX}.${BettingLimitMessageCodes.DeleteTimedWageringLimitSuccess}`
					)
				),
				tap((message) => this.handleLimitDeletionSuccess(message))
			);

		const bettingLimitFail$ = this.messageService
			.consume$({
				context: [
					BETTING_LIMIT_MESSAGE_CONTEXT.deleteBetCountLimit,
					BETTING_LIMIT_MESSAGE_CONTEXT.deleteWageringLimit,
					BETTING_LIMIT_MESSAGE_CONTEXT.deleteTimedWageringLimit,
				],
				messageType: MessageType.Error,
			})
			.pipe(
				switchMap(() =>
					this.snackBarService.openSnackBar$({
						message: "general.generic-error",
						action: "general.close",
						type: MessageType.Error,
					})
				),
				tap(() => (this.isRemoveBettingLimitLoading = false))
			);

		const resetForm$ = this.responsibleGamingFormService.resetForm$.pipe(
			tap(() => {
				this.resetForm(this.bettingLimitForm.value.limitType);
			})
		);

		this.data$$ = merge(
			timedWageringLimit$,
			activeBettingLimit$,
			queuedBettingLimit$,
			currency$,
			deleteBetCountLimitSuccess$,
			deleteWageringLimitSuccess$,
			deleteTimedWageringLimitSuccess$,
			bettingLimitFail$,
			resetForm$
		).subscribe(() => this.changeDetectorRef.markForCheck());

		this.isFormDirty$$ = this.bettingLimitForm.valueChanges.subscribe(() =>
			this.responsibleGamingFormService.sendIsFormDirty(this.bettingLimitForm.dirty)
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.isFormDirty$$?.unsubscribe();
	}

	toggleBettingLimitEdited() {
		this.isBettingLimitEdited = !this.isBettingLimitEdited;
	}

	submitRemoveBettingLimit() {
		if (this.responsibleGamingUiConfigService.showBettingLimitConfirmModal()) {
			this.openBettingLimitConfirmAlert(() => this.removeBettingLimit());
			return;
		}
		this.alertService.open(
			{
				prefixJurisdiction: true,
				id: "BETTING_LIMIT_REMOVE_ALERT_ID",
				testId: "betting-limit-deletion-confirmation",
				title: "betting-limit.confirmation-title",
				message: "betting-limit.deletion-confirmation-caption",
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => this.removeBettingLimit(),
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	submitBettingLimit() {
		if (this.responsibleGamingUiConfigService.showBettingLimitConfirmModal()) {
			this.openBettingLimitConfirmAlert(() => this.updateBettingLimit(), {
				period: this.bettingLimitForm.value.limitPeriod ?? LimitPeriod.Daily,
				limitValue: this.bettingLimitForm.value.limit,
			});
			return;
		}
		this.alertService.open(
			{
				prefixJurisdiction: true,
				id: "BETTING_LIMIT_SUBMIT_ALERT_ID",
				testId: "betting-limit-submit-confirmation",
				title: `${TRANSLATION_PREFIX}.confirmation-title`,
				message: `${TRANSLATION_PREFIX}.confirmation-caption`,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => this.updateBettingLimit(),
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	private removeBettingLimit() {
		const limitType = this.bettingLimitForm.value.limitType;
		if (limitType === BettingLimitType.BetSizeLimit) {
			this.store.dispatch(BettingLimitAction.deleteBetSizeLimit());
		} else if (limitType === BettingLimitType.WageringLimit) {
			this.store.dispatch(BettingLimitAction.deleteWageringLimit());
		} else if (limitType === BettingLimitType.TimedWageringLimit) {
			this.store.dispatch(BettingLimitAction.deleteTimedWageringLimit());
		}

		this.isRemoveBettingLimitLoading = true;
		this.resetForm(this.bettingLimitForm.value.limitType);
		this.changeDetectorRef.detectChanges();
	}

	private updateBettingLimit() {
		const limitType = this.bettingLimitForm.value.limitType;
		if (limitType === BettingLimitType.BetSizeLimit) {
			this.store.dispatch(
				BettingLimitAction.updateBetSizeLimit({
					limitValue: this.bettingLimitForm.value.limit,
				})
			);
		} else if (limitType === BettingLimitType.WageringLimit) {
			this.store.dispatch(
				BettingLimitAction.updateWageringLimit({
					amount: this.bettingLimitForm.value.limit,
				})
			);
		} else if (limitType === BettingLimitType.TimedWageringLimit) {
			this.store.dispatch(
				BettingLimitAction.updateTimedWageringLimit({
					period: LIMIT_PERIODS.get(this.bettingLimitForm.value.limitPeriod) || LimitPeriod.Daily,
					limitValue: this.bettingLimitForm.value.limit,
				})
			);
		}

		this.isSetBettingLimitLoading = true;

		this.snackBarService
			.openSnackBar$({
				prefixJurisdiction: true,
				message: `${TRANSLATION_PREFIX}.update-success`,
				action: "general.close",
				duration: 4000,
				type: MessageType.Success,
				id: "betting-limit-update-success",
			})
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();

		this.resetForm(limitType);
	}

	private openBettingLimitConfirmAlert(handler: () => void, nextLimit?: TimedBettingLimitParams) {
		this.alertService.open(
			{
				hideClose: true,
				dialogConfig: {
					disableClose: true,
					panelClass: BETTING_LIMIT_CHANGE_CLASS,
				},
				verticalButtons: true,
				mustBeInteracted: true,
				id: BETTING_LIMIT_CHANGE_DIALOG_ID,
				messageComponent: BettingLimitChangeComponent,
				messageComponentInit: (c: BettingLimitChangeComponent) => {
					c.currentLimit = this.activeBettingLimit;
					c.nextLimit = nextLimit;
				},
				buttons: [
					{
						text: "general.confirm",
						style: AbstractAlertButtonStyle.Flat,
						color: AlertButtonColor.Accent,
						handler: handler,
					},
					{
						text: "general.cancel",
						style: AbstractAlertButtonStyle.Stroked,
						color: AlertButtonColor.Primary,
					},
				],
			},
			[TranslationGroup.General]
		);
	}

	private handleLimitDeletionSuccess(message: string) {
		this.snackBarService
			.openSnackBar$({
				prefixJurisdiction: true,
				message,
				action: "general.close",
				duration: 4000,
				type: MessageType.Success,
				id: "betting-limit-delete-success",
			})
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe(() => {
				this.isSetBettingLimitLoading = false;
				this.isRemoveBettingLimitLoading = false;
			});
	}

	private getBettingLimits(getTimedWageringLimits: boolean) {
		if (getTimedWageringLimits) {
			this.store.dispatch(BettingLimitAction.getTimedWageringLimit());
			return;
		}

		this.store.dispatch(
			batchActions(
				BettingLimitAction.getBetCountLimit(),
				BettingLimitAction.getBetSizeLimit(),
				BettingLimitAction.getWageringLimit()
			)
		);
	}

	private resetForm(limitType: BettingLimitType) {
		if (
			this.activeBettingLimit &&
			this.activeBettingLimit.type === this.bettingLimitForm.value.limitType &&
			this.activeBettingLimit.limitValue >= this.bettingLimitForm.value.limit
		) {
			this.queuedBettingLimit = undefined;
		}

		this.bettingLimitForm.reset({
			limit: "",
			limitType: limitType === BettingLimitType.TimedWageringLimit ? limitType : BettingLimitType.WageringLimit,
			limitPeriod: limitType === BettingLimitType.TimedWageringLimit ? LimitPeriod.Daily : undefined,
		});

		this.isBettingLimitEdited = false;
		this.isSetBettingLimitLoading = false;
	}
}
